"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { InfoIcon, TagIcon } from "lucide-react";
import { determineSaleType } from "@/app/lib/actions/cat/sale-type";
import { saveDraftSaleTypeStep } from "@/app/lib/actions/cat/sale-type-step";
import {
  formatSaleTypeTagLabel,
  resolveSaleTypeTags,
} from "@/lib/cat/company-category-badge";
import type {
  SaleTypeCode,
  SaleTypeDecisionCondition,
  SaleTypeDecisionNode,
} from "@/lib/cat/sale-type-decision.types";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";

export interface RetailSaleTypeStepProps {
  /** Main sale type label (e.g. LLD, Retail). */
  saleTypeLabel?: string;
  /** Explanation shown under the sale type badge. */
  className?: string;
  onCanProceedChange?: (canProceed: boolean) => void;
  onBeforeNextChange?: (
    handler: (() => boolean | void | Promise<boolean | void>) | null,
  ) => void;
}

const SALE_TYPE_NODE_LABELS: Record<SaleTypeDecisionNode, string> = {
  vehiculeCarrosseCAR: "Véhicule carrossé CAR",
  vehiculeCarrosseCAR3: "Véhicule carrossé CAR3",
  nomPrenomClientFinalRenseigne: "Nom / prénom client final renseigné",
  siretClientFinalVide: "SIRET client final vide",
  vehiculeCarrosseAutreQueCARetCAR3: "Véhicule carrossé autre que CAR/CAR3",
  siretClientFinalPresentEtOK: "SIRET client final présent et valide",
  kvpsProprietaireVehiculeDOM: "Propriétaire véhicule DOM (KVPS)",
  codeAPEFinal7711: "Code APE final 7711",
  choixRENT: "Choix RENT",
  siretClientFinalPresentBaseAtomium: "SIRET client final présent dans Atomium",
  typeContratAtomium91: "Type de contrat Atomium 91",
  typeContratAtomium93: "Type de contrat Atomium 93",
  vehiculeVU: "Véhicule utilitaire (VU)",
  modeleVehiculeDansAtomiumModeleSignatureBDC:
    "Modèle véhicule présent dans Atomium / signature BDC",
  vehiculeLoue: "Véhicule loué",
  locationVWFS: "Location VWFS",
  LLD: "LLD",
  LOA: "LOA",
  siretLoueurPresentAtomium: "SIRET loueur présent dans Atomium",
  typeContrat92Loueur: "Type de contrat 92 loueur",
  LOUN: "LOUN",
  plus5VNAchat: "Plus de 5 VN à l'achat",
  achatSup1VN: "Achat supérieur à 1 VN",
  vehiculeCarrosseBranchA: "Branche A véhicule carrossé",
  vehiculeCarrosseBranchB: "Branche B véhicule carrossé",
};

function nodeToLabel(node: SaleTypeDecisionNode): string {
  return SALE_TYPE_NODE_LABELS[node] ?? node;
}

function conditionToLabel(condition: SaleTypeDecisionCondition): string {
  const base = `${nodeToLabel(condition.node)}: ${condition.answer}`;
  if (condition.source === "forced") {
    return `${base} (forcé)`;
  }
  return base;
}

export function RetailSaleTypeStep({
  className,
  onBeforeNextChange,
  onCanProceedChange,
}: RetailSaleTypeStepProps) {
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saleTypeCode, setSaleTypeCode] = useState<SaleTypeCode>("INDÉTERMINÉ");
  const [conditionsTrail, setConditionsTrail] = useState<string[]>([]);
  const [forcedNodes, setForcedNodes] = useState<SaleTypeDecisionNode[]>([]);
  const [decisionStatus, setDecisionStatus] = useState<"resolved" | "fallback">(
    "fallback",
  );

  const handleBeforeNext = useCallback(async () => {
    if (currentDraft === null) {
      return false;
    }

    if (saleTypeCode === "INDÉTERMINÉ" || decisionStatus !== "resolved") {
      return false;
    }

    const result = await saveDraftSaleTypeStep({
      id: currentDraft.id,
      saleType: saleTypeCode,
    });

    if (!result.success) {
      setErrorMessage(
        result.message ??
          "Une erreur est survenue lors de l'enregistrement de l'étape.",
      );
      return false;
    }

    if (!result.data) {
      setErrorMessage(
        "Aucune donnée brouillon n'a été retournée après l'enregistrement.",
      );
      return false;
    }

    setCurrentDraft(result.data);

    return true;
  }, [currentDraft, decisionStatus, saleTypeCode, setCurrentDraft]);

  useEffect(() => {
    onBeforeNextChange?.(handleBeforeNext);
    return () => onBeforeNextChange?.(null);
  }, [handleBeforeNext, onBeforeNextChange]);

  useEffect(() => {
    if (!currentDraft?.id) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      const result = await determineSaleType(currentDraft.id);
      if (cancelled) return;

      if (!result.success || !result.data) {
        setSaleTypeCode("INDÉTERMINÉ");
        setConditionsTrail([]);
        setForcedNodes([]);
        setDecisionStatus("fallback");
        setErrorMessage(
          result.success
            ? "Aucune donnée de détermination n'a été retournée."
            : (result.message ??
                "Erreur lors de la détermination du type de vente."),
        );
        setIsLoading(false);
        return;
      }

      const decision = result.data;
      setSaleTypeCode(decision.saleTypeCode);
      setConditionsTrail(decision.conditionsTrail.map(conditionToLabel));
      setForcedNodes(decision.forcedDecisions);
      setDecisionStatus(decision.status);
      setErrorMessage(decision.message ?? null);
      setIsLoading(false);
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [currentDraft?.id]);

  const canProceed = useMemo(
    () =>
      Boolean(currentDraft?.id) &&
      !isLoading &&
      saleTypeCode !== "INDÉTERMINÉ" &&
      decisionStatus === "resolved",
    [currentDraft?.id, decisionStatus, isLoading, saleTypeCode],
  );

  const displaySaleTypeCode = currentDraft?.id ? saleTypeCode : "INDÉTERMINÉ";
  const displayConditionsTrail = currentDraft?.id ? conditionsTrail : [];
  const displayForcedNodes = currentDraft?.id ? forcedNodes : [];
  const displayForcedNodeLabels = displayForcedNodes.map(nodeToLabel);
  const displayDecisionStatus = currentDraft?.id ? decisionStatus : "fallback";
  const isUndeterminedSaleType = displaySaleTypeCode === "INDÉTERMINÉ";
  const displayErrorMessage = currentDraft?.id
    ? errorMessage
    : "Aucun brouillon sélectionné.";

  const saleTypeTags = useMemo(
    () => resolveSaleTypeTags(currentDraft) ?? [],
    [currentDraft],
  );

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <Card className={cn(className)}>
      <CardHeader className="space-y-3 pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <TagIcon className="text-primary size-5 shrink-0" aria-hidden />
          Type de vente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 pt-2">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            Type de vente déterminé automatiquement suite à votre saisie :
          </div>
          <div className="bg-muted/30 flex flex-col gap-4 rounded-lg p-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-2">
              <div>
                <Badge
                  className={cn(
                    "rounded-md border-transparent px-4 py-1.5 text-base font-semibold text-white",
                    isUndeterminedSaleType
                      ? "bg-red-700 hover:bg-red-700/90 dark:bg-red-900 dark:hover:bg-red-900/90"
                      : "bg-blue-900 hover:bg-blue-900/90 dark:bg-blue-950 dark:hover:bg-blue-950/90",
                  )}
                >
                  {isLoading ? "Calcul en cours..." : displaySaleTypeCode}
                </Badge>
              </div>
              {saleTypeTags.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  {saleTypeTags.map((tag) => (
                    <Badge
                      key={tag}
                      className="border-border text-foreground dark:text-foreground rounded-md border bg-white px-3 py-1 text-sm font-semibold hover:bg-white/90 dark:bg-white"
                    >
                      {formatSaleTypeTagLabel(tag)}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <Alert>
          <InfoIcon className="size-4" aria-hidden />
          <AlertDescription>
            <p>
              Le type de vente est déterminé automatiquement en fonction du type
              de client et du mode de financement sélectionné.
            </p>
            <p>
              Si le type de vente déterminé ne correspond pas à votre attente,
              veuillez vérifier votre brouillon ou contacter le support.
            </p>
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div className="bg-muted/30 flex flex-col gap-4 rounded-lg p-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-2">
              <div className="text-muted-foreground flex items-start gap-1.5 text-xs">
                <InfoIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <div className="min-w-0">
                  <p className="mb-1">Raison de la détermination :</p>
                  <ul className="marker:text-muted-foreground list-disc space-y-1 pl-4">
                    {displayConditionsTrail.map((reason, index) => (
                      <li key={index}>{reason}</li>
                    ))}
                  </ul>
                  {displayForcedNodes.length > 0 ? (
                    <p className="mt-2">
                      Décisions forcées à NON:{" "}
                      {displayForcedNodeLabels.join(", ")}
                    </p>
                  ) : null}
                  {displayErrorMessage ? (
                    <p className="mt-2">{displayErrorMessage}</p>
                  ) : null}
                </div>
              </div>
            </div>
            <Badge
              variant="outline"
              className="shrink-0 border-green-600 bg-transparent text-green-700 hover:bg-green-50 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-950/30"
            >
              {displayDecisionStatus === "resolved"
                ? "Déterminé automatiquement"
                : "Résultat fallback"}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
